import React from 'react';
import Icon from '../shared/Icon';
import ListToolbar, { ToolbarSearch } from '../shared/ListToolbar';
import { fmtDate, fmtDateTime, getOrgaoDisplay, getModoDisputaBadge, getStructureBadge, formatNumeroCompraComAno, getProrrogacao, isLicitacaoOver } from '../../utils/formatters';
import { pncpPublicUrl, comprasnetPublicUrl } from '../../utils/procurementEnums';
import { PORTAL_MAP, fmt, getStatusInfo, isMuralSource, getDispensaBadge, operationalPortalUrl } from './procurementUi';
import { campo } from '../../design';

/**
 * Tabela do Encontrar (Banco de Licitações).
 *
 * Piloto da Onda 3 da padronização: primeira tela extraída do
 * `LicitacoesTab.tsx`, que tinha 12.447 linhas concentrando 8 telas e 26
 * superfícies flutuantes. Enquanto tudo mora num arquivo só, tela nova nasce
 * copiando o vizinho em vez do componente compartilhado — foi assim que o
 * módulo acumulou três anatomias de cabeçalho e 113 `window.confirm`.
 *
 * A extração foi feita com o JSX movido VERBATIM: as props são desestruturadas
 * com os mesmos nomes que as variáveis tinham no componente pai, então o corpo
 * não mudou um caractere. É o que torna o piloto reversível e conferível por
 * diff — a alternativa (renomear 40 identificadores dentro de 580 linhas de
 * JSX) trocaria risco de layout por risco de comportamento.
 *
 * Escolhido como piloto por ter o MENOR acoplamento entre os blocos grandes:
 * 40 dependências do pai, contra 61 do painel de detalhe, 94 do drawer de
 * proposta e ~189 do modal de automação.
 */
export interface EncontrarTableProps {
  portalsWithAccess: Set<string>;
  SortHeader: React.FC<any>;
  dashboard: any;
  dupMap: Map<number, any>;
  dupSuggestions: any;
  editingDateId: any;
  editingDateValue: any;
  handleBulkDiscard: (...args: any[]) => any;
  handleCreateProposalFromList: (...args: any[]) => any;
  handleDiscardItem: (...args: any[]) => any;
  handleRefreshSituacao: (...args: any[]) => any;
  handleSaveDate: (...args: any[]) => any;
  handleStatusChange: (...args: any[]) => any;
  isSituacaoBlockingEvent: (item: any) => boolean;
  isSituacaoOverdue: (item: any) => boolean;
  items: any[];
  itemsMeta: any;
  itemsPage: number;
  loading: boolean;
  openDetail: (...args: any[]) => any;
  preparingProposal: boolean;
  readItemIds: Set<number>;
  refreshingSituacaoId: any;
  scraperPendingIds: Set<number>;
  searchIdFilter: string;
  searchInput: string;
  searches: any[];
  selectedIds: Set<number>;
  setEditingDateId: (v: any) => void;
  setEditingDateValue: (v: any) => void;
  setItemsPage: (v: any) => void;
  setSearchIdFilter: (v: any) => void;
  setSearchInput: (v: any) => void;
  setShowDupReview: (v: any) => void;
  setStatusFilter: (v: any) => void;
  setTrashMode: (v: any) => void;
  statusFilter: string;
  toggleSelectAll: (...args: any[]) => any;
  toggleSelectItem: (...args: any[]) => any;
  trashMode: boolean;
}

const EncontrarTable: React.FC<EncontrarTableProps> = ({
  portalsWithAccess,
  SortHeader,
  dashboard,
  dupMap,
  dupSuggestions,
  editingDateId,
  editingDateValue,
  handleBulkDiscard,
  handleCreateProposalFromList,
  handleDiscardItem,
  handleRefreshSituacao,
  handleSaveDate,
  handleStatusChange,
  isSituacaoBlockingEvent,
  isSituacaoOverdue,
  items,
  itemsMeta,
  itemsPage,
  loading,
  openDetail,
  preparingProposal,
  readItemIds,
  refreshingSituacaoId,
  scraperPendingIds,
  searchIdFilter,
  searchInput,
  searches,
  selectedIds,
  setEditingDateId,
  setEditingDateValue,
  setItemsPage,
  setSearchIdFilter,
  setSearchInput,
  setShowDupReview,
  setStatusFilter,
  setTrashMode,
  statusFilter,
  toggleSelectAll,
  toggleSelectItem,
  trashMode,
}) => (
    <div>
      {/* Toolbar no padrão do guia §3b (ListToolbar), a mesma do Cadastrar.
          Era artesanal aqui: input `bg-slate-700/50` (fora da §2), busca
          esticando com flex-1 e os botões enfileirados junto dos filtros —
          navegando entre Procurar e Cadastrar a barra mudava de forma. */}
      <div className="mb-4">
        <ListToolbar
          search={(
            <ToolbarSearch
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Objeto, orgao, municipio, no, UASG, processo..."
              widthClass="w-80"
            />
          )}
          filters={!trashMode ? (
            <>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setItemsPage(1); }}
                className={`rounded-lg px-3 py-2 text-sm focus:outline-none ${campo}`}
              >
                {/* Só o funil do Encontrar. O select oferecia os 11 status do
                    sistema, mas os de participação/terminais pertencem ao
                    Cadastrar e ao Monitorar, e a lista aqui filtra
                    hasProposal=false — escolher "Homologado" devolvia vazio. */}
                <option value="">Todos os status</option>
                <option value="abertas">Abertas (triagem)</option>
                <option value="vencidas">Vencidas (prazo passou)</option>
                <option value="suspensa">Suspensa</option>
                <option value="revogada">Revogada</option>
              </select>
              {/* Filtro de perfil só aparece quando há mais de um pra escolher:
                  com um único perfil cadastrado o select ocupava espaço da barra
                  oferecendo uma opção só. */}
              {searches.length > 1 && (
                <select
                  value={searchIdFilter}
                  onChange={(e) => { setSearchIdFilter(e.target.value); setItemsPage(1); }}
                  className={`rounded-lg px-3 py-2 text-sm focus:outline-none ${campo}`}
                >
                  <option value="">Todos os perfis</option>
                  {searches.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
            </>
          ) : undefined}
          actions={(
            <>
              {!trashMode && (<>
              {/* Botão "Duplicatas": abre painel lateral com todos os pares fuzzy
                  pra revisão em batch (Juntar / Não é duplicata por par).
                  Contador exibe número de pares (dupMap indexa cada par duas
                  vezes — uma por lado). */}
              <button
                onClick={() => setShowDupReview(true)}
                className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${dupSuggestions.length > 0 ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30' : 'bg-central-cartao border border-central-borda text-slate-500'}`}
                disabled={dupSuggestions.length === 0}
                title="Abrir painel de revisão de duplicatas"
              >
                <Icon name="alertTriangle" className="w-4 h-4" />
                Duplicatas{dupSuggestions.length > 0 ? ` (${dupSuggestions.length})` : ''}
              </button>
              </>)}
              <button
                onClick={() => { setTrashMode(!trashMode); setStatusFilter(''); setItemsPage(1); }}
                className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${trashMode ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-central-cartao border border-central-borda text-slate-400 hover:text-red-400'}`}
              >
                <Icon name="trash" className="w-4 h-4" />
                Lixeira{dashboard.descartado ? ` (${dashboard.descartado})` : ''}
              </button>
            </>
          )}
          secondRow={!trashMode && selectedIds.size > 0 ? (
            <button
              onClick={handleBulkDiscard}
              className="px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
            >
              <Icon name="trash" className="w-4 h-4" />
              Descartar {selectedIds.size} selecionada(s)
            </button>
          ) : undefined}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-central-cartao rounded-central border border-central-borda max-h-[calc(100vh-260px)] overflow-y-auto">
        {/* table-fixed (09/08): no layout AUTO o navegador distribuía a sobra
            pela largura do CONTEÚDO (nomes de órgão longos engoliam o espaço e
            o Objeto ficava espremido). Com fixed, os w-* do thead valem e o
            Objeto (única coluna sem largura) herda TODO o espaço livre. */}
        <table className="w-full text-sm table-fixed">
          <thead className="sticky top-0 z-20 bg-central-fundo/95 backdrop-blur border-b border-central-borda">
            <tr>
              <th className="px-3 py-2.5 text-center w-8">
                <input
                  type="checkbox"
                  checked={items.length > 0 && selectedIds.size === items.length}
                  onChange={toggleSelectAll}
                  className="rounded border-slate-600 bg-slate-700 text-primary focus:ring-primary"
                />
              </th>
              {/* Receita Monitorar (09/08, decisão do user): Portal e Nº/Mod viraram
                  sub-linha do Órgão — 9→7 colunas; troca de portal foi pro modal
                  de detalhes (operação rara; o select na linha vazava e arriscava
                  troca acidental). Objeto herda o espaço. */}
              <SortHeader column="orgaoNome" label="Órgão / UF" className="text-left w-72" />
              <SortHeader column="objetoCompra" label="Objeto" className="text-left" />
              <SortHeader column="valorEstimado" label="Valor Est." className="text-right w-28" />
              <SortHeader column="status" label="Status" className="text-center w-28" />
              {/* Cadastro e Sessão em colunas SEPARADAS (18/08). Estavam na mesma
                  célula, uma linha embaixo da outra, e na esmagadora maioria das
                  licitações as duas datas coincidem — o olho aprende a tratar
                  como uma coisa só. Quando divergem, que é o caso perigoso, a
                  diferença não chamava atenção nenhuma: a CC 07/2026 do SANEAR
                  (R$ 519.087,87) tinha cadastro até 17/08 17:00 e sessão em
                  18/08 09:00; quem cadastra "as de amanhã" na noite anterior
                  chegava com o prazo já vencido. */}
              <SortHeader column="dataEncerramento" label="Cadastro até" className="text-center w-32" />
              <SortHeader column="proximoEvento" label="Sessão" className="text-center w-32" />
              <th className="px-1 py-2.5 text-center text-xs font-medium text-slate-400 uppercase w-20">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-central-borda">
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">Nenhuma licitação encontrada</td></tr>
            ) : (
              items.map((item) => {
                const statusInfo = getStatusInfo(item.status);
                const isRead = readItemIds.has(item.id);
                const rowStyle: React.CSSProperties = selectedIds.has(item.id) ? { backgroundColor: 'rgba(249,115,22,0.12)' }
                  : item.status === 'participando' ? { backgroundColor: 'rgba(168,85,247,0.18)' }
                  : item.status === 'interessado' ? { backgroundColor: 'rgba(59,130,246,0.15)' }
                  : item.status === 'analisando' ? { backgroundColor: 'rgba(234,179,8,0.15)' }
                  : item.status === 'ganho' ? { backgroundColor: 'rgba(16,185,129,0.18)' }
                  : item.status === 'perdido' ? { backgroundColor: 'rgba(239,68,68,0.15)' }
                  : item.status === 'desistida' ? { backgroundColor: 'rgba(244,63,94,0.15)' }
                  : {};
                return (
                  <tr key={item.id} style={{ ...rowStyle, ...(isRead && item.status === 'novo' ? { opacity: 0.55 } : {}) }} className="hover:bg-central-elevado transition-colors group">
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelectItem(item.id)}
                        onClick={(e) => e.shiftKey && toggleSelectItem(item.id, e)}
                        className="rounded border-slate-600 bg-slate-700 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <div
                          className="flex-1 min-w-0 font-medium text-white text-xs leading-tight truncate cursor-pointer"
                          title={getOrgaoDisplay(item)}
                          onClick={(e) => toggleSelectItem(item.id, e)}
                        >
                          {getOrgaoDisplay(item) || '—'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-slate-500 text-[11px]">{item.municipio ? `${item.municipio}/${item.uf}` : item.uf || ''}</div>
                        <div className="flex items-center gap-1 shrink-0">
                          {/* Badges movidos pra cá (próximos do trash) pra liberar
                              espaço no nome do orgão. Sempre visíveis (não dependem
                              do hover, ao contrário dos action icons abaixo).
                              UX 08/08: nome virou flex-1 min-w-0 truncate → a coluna
                              respeita o w-* do header (antes o min-content de
                              280px+badges empurrava a tabela pra fora da tela). */}
                          {(() => {
                            if (!item.createdAt) return null;
                            const ageMs = Date.now() - new Date(item.createdAt).getTime();
                            if (ageMs > 24 * 3600_000) return null;
                            return (
                              <span className="shrink-0 px-1.5 py-0.5 bg-slate-600/30 text-slate-300 rounded-full text-[10px] font-semibold" title="Encontrada nas últimas 24h">
                                novo
                              </span>
                            );
                          })()}
                          {item.excludedFromPncpAt && (
                            <span
                              className="shrink-0 px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded-full text-[10px] font-medium"
                              title="Excluída no PNCP — possível republicação. Abra os detalhes."
                            >
                              ⚠ PNCP
                            </span>
                          )}
                          {dupMap.has(item.id) && (
                            <span
                              className="shrink-0 px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded-full text-[10px] font-medium"
                              title={`Possível duplicata (${dupMap.get(item.id)!.twin.portalOrigem}). Abra os detalhes pra confirmar.`}
                            >
                              ⚠ duplicata
                            </span>
                          )}
                          <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                            {(() => {
                              const repubUrl = item.excludedFromPncpAt ? item.republicationCandidate?.pncpUrl : null;
                              const opUrl = operationalPortalUrl(item);
                              const cnetUrl = comprasnetPublicUrl(item);
                              const pncpUrl = pncpPublicUrl(item.pncpId);
                              const href = repubUrl || opUrl || cnetUrl || pncpUrl || item.linkOrigem;
                              if (!href) return null;
                              const title = repubUrl
                                ? 'Abrir republicação no PNCP (link original foi excluído)'
                                : opUrl
                                  ? 'Abrir no portal (PCP/BLL)'
                                  : cnetUrl
                                    ? 'Abrir no ComprasNet (página pública)'
                                    : pncpUrl
                                      ? 'Abrir no PNCP'
                                      : 'Abrir no portal';
                              return (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className={`p-0.5 transition-colors ${repubUrl ? 'text-amber-400 hover:text-amber-300' : 'text-slate-500 hover:text-white'}`}
                                  title={title}
                                >
                                  <Icon name="externalLink" className="w-3.5 h-3.5" />
                                </a>
                              );
                            })()}
                            {!trashMode && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDiscardItem(item.id, !!item.temProposta); }}
                                className="p-0.5 text-slate-500 hover:text-red-400 transition-colors"
                                title="Descartar"
                              >
                                <Icon name="trash" className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Receita Monitorar (09/08): Nº/Mod + Portal como SUB-LINHA do
                          Órgão (eram 2 colunas). Troca de portal: modal de detalhes. */}
                      {(() => {
                        const mod = item.modalidadeNome?.includes('Pregão') || item.modalidadeNome?.includes('Pregao') ? 'PE'
                          : item.modalidadeNome?.includes('Dispensa') ? 'DE'
                          : item.modalidadeNome?.includes('Concorrência') || item.modalidadeNome?.includes('Concorrencia') ? 'CC'
                          : item.modalidadeNome ? item.modalidadeNome.substring(0, 4) : '';
                        const md = getModoDisputaBadge(item.modoDisputa);
                        const pInfo = PORTAL_MAP[item.portalOrigem];
                        const pLabel = pInfo?.label || (isMuralSource(item) ? '📰 Mural/Diário' : item.portalOrigem || 'PNCP');
                        const db = getDispensaBadge(item);
                        return (
                          <div className="flex items-center gap-1.5 mt-1 text-[10px] flex-wrap">
                            {mod && <span className={`px-1 py-0 rounded ${mod === 'PE' ? 'bg-blue-500/20 text-blue-300' : mod === 'DE' ? 'bg-green-500/20 text-green-300' : 'bg-slate-600/50 text-slate-300'}`}>{mod}</span>}
                            {item.numeroCompra && <span className="text-slate-300">{formatNumeroCompraComAno(item.numeroCompra, item.anoCompra)}</span>}
                            {md && <span className={`text-[9px] px-1 py-0 rounded ${md.color}`} title={`Modo de disputa: ${md.full}`}>{md.short}</span>}
                            {/* O ✓ aqui é o MESMO da lista de portais e sempre
                                significou "tem credencial em Acessos" — mas era
                                lido como "tem automação", que é outra coisa. O
                                título passa a dizer qual das duas. */}
                            <span
                              className={`truncate max-w-[110px] ${pInfo?.color || 'text-slate-500'}`}
                              title={
                                `Portal: ${pLabel}`
                                + (portalsWithAccess.has(item.portalOrigem)
                                    ? ' — ✓ tem credencial cadastrada em Acessos (não significa automação)'
                                    : '')
                                + ' — pra trocar, abra os detalhes (lupa)'
                              }
                            >
                              {portalsWithAccess.has(item.portalOrigem) ? '✓ ' : ''}{pLabel}
                            </span>
                            {db && <span className={`text-[9px] px-1 py-0 rounded border ${db.color} whitespace-nowrap`} title={db.title}>{db.label}</span>}
                            {item.pcpInversaoFase && <span className="text-[9px] px-1 py-0 rounded bg-amber-500/20 text-amber-300" title="Inversão de fase: habilitação antes da proposta — envio automático não cobre o upload">↻</span>}
                            {item.pcpGarantiaContratual && <span className="text-[9px] px-1 py-0 rounded bg-rose-500/20 text-rose-300" title="Exige Comprovante de Seguro Garantia — anexar manualmente">🛡</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => openDetail(item)}
                        className="text-left text-xs text-slate-300 hover:text-white transition-colors line-clamp-2"
                        title={item.objetoCompra}
                      >
                        {item.objetoCompra || '—'}
                      </button>
                      {item.matchedKeywords?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.matchedKeywords.slice(0, 3).map((kw: string, i: number) => (
                            <span key={i} className="px-1.5 py-0.5 bg-slate-600/30 text-slate-300 rounded text-[10px]">{kw}</span>
                          ))}
                          {item.matchedKeywords.length > 3 && (
                            <span className="px-1 py-0.5 text-slate-500 text-[10px]" title={item.matchedKeywords.slice(3).join(', ')}>
                              +{item.matchedKeywords.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-xs font-medium text-slate-200 whitespace-nowrap">{fmt(item.valorEstimado)}</td>
                    <td className="px-3 py-2 text-center">
                      {trashMode ? (
                        <button
                          onClick={() => handleStatusChange(item.id, 'novo')}
                          className="text-xs px-2 py-1 rounded-full bg-slate-600/30 text-slate-200 hover:bg-slate-600/60 transition-colors"
                        >
                          Restaurar
                        </button>
                      ) : (
                        // UX 08/08: badges EMPILHADOS sob o select (flex-col). Na horizontal,
                        // select + "verif. Xd" + "Vencido Xd" estouravam a coluna w-32 e
                        // empurravam a tabela pra fora da tela (corte do Próximo evento/Ações).
                        <div className="flex flex-col items-center gap-0.5">
                          {/* Status é LEITURA aqui. Era um select de 11 opções por
                              linha, mas em toda a história do sistema `analisando`
                              foi usado em 2 itens e `interessado` em 0: a triagem
                              real é cadastrar proposta ou descartar, e as duas já
                              são botões na coluna Ações. Quem manda no resto é o
                              portal (suspensa/revogada chegam pelo scrape). */}
                          <span className={`text-xs px-2 py-1 rounded-full w-full max-w-[112px] text-center truncate ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                          {/* 24/05/2026 — A4: badge "🔄 verificando" pros items enfileirados no scraper aguardando callback */}
                          {scraperPendingIds.has(item.id) && (
                            <span
                              className="text-[9px] px-1.5 py-0.5 rounded bg-slate-600/30 text-slate-300 whitespace-nowrap inline-flex items-center gap-1"
                              title="Enfileirado no scraper — aguardando callback assíncrono (5-15min). Status real virá em background."
                            >
                              <Icon name="refresh" className="w-2.5 h-2.5 animate-spin" />
                              verificando
                            </span>
                          )}
                          {item.situacao && /revogad|cancelad|desert|anulad|fracassad/i.test(item.situacao) && item.status !== 'revogada' && item.status !== 'perdido' && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 whitespace-nowrap">{item.situacao}</span>
                          )}
                          {item.situacao && /suspens/i.test(item.situacao) && item.status !== 'suspensa' && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 whitespace-nowrap">{item.situacao}</span>
                          )}
                          {/* A2: badge "Vencido há Xd" pros items em status ATIVO cuja
                              sessão acabou há +3d — sinaliza que algo provavelmente já
                              foi decidido no portal mas o Hub ainda não atualizou. */}
                          {(() => {
                            const ACTIVE = ['novo', 'analisando', 'interessado', 'participando', 'aguardando_julgamento'];
                            if (!ACTIVE.includes(item.status)) return null;
                            if (!item.dataEncerramento) return null;
                            const daysOverdue = Math.floor((Date.now() - new Date(item.dataEncerramento).getTime()) / 86400000);
                            if (daysOverdue < 1) return null;
                            const cls = daysOverdue >= 7
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-amber-500/20 text-amber-400';
                            return (
                              <span
                                className={`text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap ${cls}`}
                                title={`Sessão encerrou há ${daysOverdue} dia(s) mas status ainda está "${item.status}". Clique "Verificar vencidos" no Cadastrar/Monitorar pra forçar re-consulta PNCP+portal.`}
                              >
                                ⚠ Vencido {daysOverdue}d
                              </span>
                            );
                          })()}
                          {/* Staleness da verificação (auditoria 04/07): itens de portal
                              sem integração PNCP ficam fora do re-enrich — sinaliza pro
                              user conferir a fonte antes de confiar no "Aberto". */}
                          {(() => {
                            const ACTIVE = ['novo', 'analisando', 'interessado'];
                            if (!ACTIVE.includes(item.status)) return null;
                            // Duas fontes verificam o item e cada uma carimba o seu
                            // relógio: o PNCP em `pncpLastCheckedAt`, o portal em
                            // `lastMonitoredAt`. Olhando só o primeiro, a linha dizia
                            // "sem verificação" em item BLL/PCP que o scrape consulta
                            // três vezes por dia. O que interessa ao operador é quando
                            // foi a ÚLTIMA vez que alguém olhou, seja quem for.
                            const relogios = [item.pncpLastCheckedAt, item.lastMonitoredAt]
                              .filter(Boolean)
                              .map((d: any) => new Date(d).getTime())
                              .filter((t: number) => Number.isFinite(t));
                            const last = relogios.length ? Math.max(...relogios) : null;
                            const days = last ? Math.floor((Date.now() - last) / 86400000) : null;
                            if (days !== null && days < 5) return null;
                            return (
                              <span
                                className="text-[9px] px-1.5 py-0.5 rounded bg-slate-600/40 text-slate-400 whitespace-nowrap"
                                title={days === null
                                  ? 'Este item nunca passou por verificação automática — nem PNCP, nem scrape de portal. Sem segunda fonte, o status é o que o agregador disse no dia em que chegou: confira na fonte antes de decidir.'
                                  : `Última verificação automática (PNCP ou portal) há ${days} dias. O status pode estar desatualizado — confira na fonte.`}
                              >
                                🕐 {days === null ? 'sem verificação' : `verif. ${days}d`}
                              </span>
                            );
                          })()}
                          {item.situacaoCompra && isSituacaoBlockingEvent(item.situacaoCompra) && (() => {
                            const overdue = isSituacaoOverdue(item.situacaoCompra);
                            const prev = item.situacaoCompra.ultimoAviso?.previsaoReabertura;
                            return (
                              <span
                                className={`text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap cursor-help inline-flex items-center gap-1 ${
                                  overdue ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                                }`}
                                title={
                                  overdue
                                    ? `Previsão de reabertura ${prev} venceu — portal ainda bloqueado. Clique no ícone para re-verificar.`
                                    : item.situacaoCompra.ultimoAviso?.mensagem || 'Cadastro bloqueado pelo ComprasNet — evento pendente de publicação'
                                }
                              >
                                {overdue ? '⛔ Vencida' : '⚠ Suspensa'}
                                {prev ? ` — ${overdue ? 'prev.' : 'reabre'} ${prev.slice(0, 5)}` : ''}
                                <button
                                  onClick={(e) => handleRefreshSituacao(item.id, e)}
                                  disabled={refreshingSituacaoId === item.id}
                                  className="text-current hover:opacity-70 disabled:opacity-40 ml-0.5"
                                  title="Verificar status no portal agora"
                                >
                                  <svg className={`w-3 h-3 ${refreshingSituacaoId === item.id ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </button>
                              </span>
                            );
                          })()}
                        </div>
                      )}
                      {/* indicadores: proposta/monitorado/disputa (antiga col. Vinc) */}
                      <div className="flex items-center justify-center gap-0.5 mt-1">
                        {item._proposalCount > 0 && (
                          <span className={`text-[9px] px-1 py-0.5 rounded cursor-default ${
                            item._proposalStatus === 'enviada' || item._proposalStatus === 'aceita' ? 'bg-slate-600/30 text-slate-200 font-semibold'
                            : 'bg-slate-600/30 text-slate-400'
                          }`} title={`Proposta: ${item._proposalStatus || 'rascunho'}`}>P</span>
                        )}
                        {item.monitored && (
                          <span className="text-[9px] px-1 py-0.5 rounded bg-slate-600/30 text-slate-300 cursor-default" title="Monitoramento ativo">M</span>
                        )}
                        {item._disputeCount > 0 && (
                          <span className={`text-[9px] px-1 py-0.5 rounded cursor-default ${
                            item._disputeStatus === 'ativo' ? 'bg-cyan-500/20 text-cyan-300'
                            : item._disputeStatus === 'finalizado' ? 'bg-slate-600/30 text-slate-400'
                            : 'bg-amber-500/20 text-amber-300'
                          }`} title={`Disputa: ${item._disputeStatus || '—'}`}>D</span>
                        )}
                      </div>
                    </td>
                    {(() => {
                      // Próximo evento = menor entre dataEncerramento e dataSessaoInicio.
                      // (As 4 colunas de data foram fundidas aqui; abertura vai no tooltip,
                      // duplo-clique edita o encerramento.)
                      // 2 janelas distintas (ComprasNet dispensa): Proposta (entrega)
                      // = dataAbertura→dataEncerramento; Sessão/Lances = dataSessaoInicio→Fim.
                      const enc = item.dataEncerramento ? new Date(item.dataEncerramento) : null;
                      const ss = item.dataSessaoInicio ? new Date(item.dataSessaoInicio) : null;
                      const sf = item.dataSessaoFim ? new Date(item.dataSessaoFim) : null;
                      const fmtHM = (x: Date) => x.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
                      // dd/mm sem ano (UX 08/08 — o ano estourava a largura da coluna; completo no tooltip)
                      const fmtDM = (x: Date) => x.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });
                      // próximo evento FUTURO (pro badge "em Xd"): o mais próximo que ainda não passou
                      const cands = [enc, ss].filter(Boolean) as Date[];
                      const fut = cands.filter(d => d.getTime() > Date.now()).sort((a, b) => a.getTime() - b.getTime());
                      const next = fut[0] || (cands.length ? [...cands].sort((a, b) => b.getTime() - a.getTime())[0] : null);
                      // Badge "Prorrogada": encerramento adiado. Esconde nas encerradas.
                      const prorrog = getProrrogacao(item);
                      const showProrrog = !!prorrog && !isLicitacaoOver(item);
                      // F-10 (Onda 2): datetime-local PRESERVANDO a hora — o input type=date
                      // salvava só YYYY-MM-DD e um encerramento 14:00 virava meia-noite
                      // (esse campo alimenta o pré-preenchimento da disputa e o relógio).
                      const startEdit = () => {
                        setEditingDateId(item.id);
                        if (item.dataEncerramento) {
                          const d = new Date(item.dataEncerramento);
                          const pad = (n: number) => String(n).padStart(2, '0');
                          setEditingDateValue(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                        } else {
                          setEditingDateValue('');
                        }
                      };
                      // A1: "Verificado há Xd" — sinaliza quando a última consulta ao
                      // PNCP/portal foi feita. Cor por idade: verde<3d, amarelo<7d,
                      // vermelho>7d. Quando vazio, é um item nunca re-checado.
                      // Mesmo raciocínio do selo 🕐: quem verificou por último vale,
                      // seja o PNCP ou o scrape do portal.
                      const chkTs = [item.lastMonitoredAt, item.pncpLastCheckedAt]
                        .filter(Boolean)
                        .map((d: any) => new Date(d).getTime())
                        .filter((t: number) => Number.isFinite(t));
                      const lastChk = chkTs.length ? new Date(Math.max(...chkTs)) : null;
                      const lastChkH = lastChk ? (Date.now() - lastChk.getTime()) / 3600000 : null;
                      let lastChkLbl: string | null = null;
                      let lastChkCls = 'text-slate-500';
                      if (lastChkH !== null) {
                        if (lastChkH < 1) lastChkLbl = 'chk agora';
                        else if (lastChkH < 24) lastChkLbl = `chk ${Math.round(lastChkH)}h`;
                        else lastChkLbl = `chk ${Math.floor(lastChkH / 24)}d`;
                        if (lastChkH > 24 * 7) lastChkCls = 'text-red-400/80';
                        else if (lastChkH > 24 * 3) lastChkCls = 'text-amber-400/70';
                        else lastChkCls = 'text-slate-500';
                      }
                      const lastChkTitle = lastChk
                        ? `Última verificação PNCP/portal: ${fmtDateTime(lastChk.toISOString())}`
                        : 'Nunca verificado — clique "Verificar status" no detalhe';
                      if (editingDateId === item.id) {
                        return (
                          <td className="px-2 py-2 text-center">
                            <input type="datetime-local" value={editingDateValue} onChange={e => setEditingDateValue(e.target.value)} onBlur={() => handleSaveDate(item.id)} onKeyDown={e => { if (e.key === 'Enter') handleSaveDate(item.id); if (e.key === 'Escape') setEditingDateId(null); }} autoFocus className="w-44 px-1 py-0.5 bg-slate-700 border border-primary text-white rounded text-xs text-center" />
                          </td>
                        );
                      }
                      if (!enc && !ss) {
                        return (
                          <td className="px-2 py-2 text-center text-xs whitespace-nowrap" onDoubleClick={startEdit} title="Duplo clique pra editar encerramento">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-slate-600">—</span>
                              {lastChkLbl && <span className={`text-[9px] ${lastChkCls}`} title={lastChkTitle}>{lastChkLbl}</span>}
                            </div>
                          </td>
                        );
                      }
                      const h = (next.getTime() - Date.now()) / 3600000;
                      // dias por CALENDÁRIO (não floor(h/24)) pra evitar "em 1d" quando
                      // ainda há 2 madrugadas no meio (hoje 16h → enc depois de amanhã 09h).
                      const nowD = new Date();
                      const startEnc = new Date(next.getFullYear(), next.getMonth(), next.getDate()).getTime();
                      const startNow = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate()).getTime();
                      const calDays = Math.round((startEnc - startNow) / 86400000);
                      let cls = 'bg-slate-700/50 text-slate-400'; let lbl = '';
                      if (h < 0) { cls = 'bg-slate-700/50 text-slate-500'; lbl = 'passou'; }
                      else if (h < 2) { cls = 'bg-red-500/30 text-red-300 font-semibold'; lbl = `em ${Math.max(1, Math.round(h * 60))}min`; }
                      else if (h < 24) { cls = 'bg-red-500/20 text-red-300 font-semibold'; lbl = `em ${Math.round(h)}h`; }
                      else { cls = 'bg-slate-700/40 text-slate-300'; lbl = `em ${calDays}d`; }
                      return (
                        <>
                        {/* COLUNA 1 — Cadastro até (fim do recebimento de propostas).
                            É o prazo de AÇÃO: passou daqui, não dá mais pra enviar,
                            mesmo com a sessão dias à frente. Estava junto da sessão
                            na mesma célula e, como na maioria das licitações as duas
                            datas coincidem, o olho tratava como uma coisa só — a CC
                            07/2026 do SANEAR (R$ 519.087,87) fechava cadastro em
                            17/08 17:00 com sessão em 18/08 09:00, e quem cadastra
                            "as de amanhã" na véspera chegou com o prazo vencido. */}
                        <td className="px-2 py-2 text-[11px] whitespace-nowrap text-center" onDoubleClick={startEdit} title={`Fim do recebimento de propostas. Duplo clique edita. Abertura proposta: ${item.dataAbertura ? fmtDateTime(item.dataAbertura) : '—'}`}>
                          {enc ? (() => {
                            const hEnc = (enc.getTime() - Date.now()) / 3600000;
                            const diverge = !!ss && Math.abs(ss.getTime() - enc.getTime()) > 3600_000;
                            const cor = hEnc < 0 ? 'text-slate-500'
                              : hEnc < 24 ? 'text-red-300 font-semibold'
                              : 'text-slate-200';
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className={cor}>{fmtDM(enc)} {fmtHM(enc)}</span>
                                {hEnc < 0 ? (
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-slate-700/50 text-slate-500">encerrado</span>
                                ) : (
                                  <span className={`text-[9px] px-1 py-0.5 rounded ${hEnc < 24 ? 'bg-red-500/25 text-red-300 font-semibold' : 'bg-slate-700/50 text-slate-400'}`}>
                                    {hEnc < 2 ? `em ${Math.max(1, Math.round(hEnc * 60))}min` : hEnc < 48 ? `em ${Math.round(hEnc)}h` : `em ${Math.floor(hEnc / 24)}d`}
                                  </span>
                                )}
                                {diverge && hEnc >= 0 && (
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 font-medium"
                                        title="O cadastro fecha ANTES do dia da sessão — não dá pra deixar pra véspera da disputa">
                                    fecha antes
                                  </span>
                                )}
                              </div>
                            );
                          })() : (
                            // Sem `dataEncerramento` publicado. Um "—" seco aqui se lê
                            // como "não tem prazo", e não é isso: em 24 dos 25 casos
                            // medidos em 24/08/2026 existe data de SESSÃO, e o cadastro
                            // fecha nela ou antes. Dizer o que se sabe (e o que não se
                            // sabe) evita a leitura de que dá para deixar para depois.
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-slate-600">—</span>
                              <span
                                className="text-[9px] px-1 py-0.5 rounded bg-slate-700/50 text-slate-400"
                                title={ss
                                  ? `O portal não publicou o fim do recebimento de propostas. Trate a sessão (${fmtDM(ss)}) como limite: o cadastro fecha nela ou antes.`
                                  : 'O portal não publicou nem o fim do recebimento nem a data da sessão. Confira no portal antes de planejar.'}
                              >
                                {ss ? 'prazo = sessão' : 'sem prazo'}
                              </span>
                            </div>
                          )}
                        </td>
                        {/* COLUNA 2 — Sessão/disputa e os demais sinais da linha. */}
                        <td className="px-2 py-2 text-[11px] whitespace-nowrap" title={`Duplo clique na coluna "Cadastro até" edita o encerramento · ${lastChkTitle}`}>
                          <div className="flex flex-col items-start gap-0.5">
                            {/* UX 08/08: data SEM ano (dd/mm hh:mm) — com ano as linhas
                                nowrap passavam de 180px e estouravam o w-40 da coluna.
                                Ano completo fica no tooltip do td (duplo-clique edita). */}
                            {ss && (() => {
                              // Hora 00:00 = portal só informou a DATA da sessão — mostrar
                              // "00:00" parecia horário real (P1 auditoria 04/07).
                              const hm = fmtHM(ss);
                              const hmTxt = hm === '00:00' ? '' : ` ${hm}${sf ? `–${fmtHM(sf)}` : ''}`;
                              return <span className="text-slate-200" title={hm === '00:00' ? 'Sessão pública — portal informou só a data (sem horário)' : `Sessão pública / envio de lances: ${fmtDateTime(ss)}`}>⚖️ Sessão {fmtDM(ss)}{hmTxt}</span>;
                            })()}
                            <div className="flex items-center gap-1 flex-wrap">
                              {lbl && <span className={`px-1.5 py-0.5 rounded text-[10px] ${cls}`}>{lbl}</span>}
                              {showProrrog && <span className="px-1 py-0.5 rounded text-[9px] bg-amber-500/15 text-amber-300 border border-amber-700/40" title={`Prorrogada: ${fmtDateTime(prorrog!.from)} → ${fmtDateTime(prorrog!.to)}`}>⏱ Prorrogada</span>}
                              {lastChkLbl && <span className={`text-[9px] ${lastChkCls}`}>{lastChkLbl}</span>}
                            </div>
                          </div>
                        </td>
                        </>
                      );
                    })()}
                    <td className="px-1 py-2 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        <button onClick={() => openDetail(item)} className="p-1 text-slate-400 hover:text-white transition-colors" title="Ver detalhes">
                          <Icon name="search" className="w-3.5 h-3.5" />
                        </button>
                        {!trashMode && ['novo', 'analisando', 'interessado', 'participando'].includes(item.status) && (() => {
                          const blocked = isSituacaoBlockingEvent(item.situacaoCompra);
                          const blockedTitle = blocked
                            ? `Cadastro bloqueado — ${item.situacaoCompra.ultimoAviso?.mensagem || 'evento pendente de publicação no ComprasNet'}`
                            : 'Cadastrar Proposta';
                          return (
                            <button
                              onClick={() => !blocked && handleCreateProposalFromList(item)}
                              disabled={blocked || preparingProposal}
                              className={`p-1 transition-colors ${blocked || preparingProposal ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white'}`}
                              title={preparingProposal ? 'Buscando itens no PNCP...' : blockedTitle}
                            >
                              <Icon name={preparingProposal ? 'refresh' : 'document'} className={`w-3.5 h-3.5 ${preparingProposal ? 'animate-spin' : ''}`} />
                            </button>
                          );
                        })()}
                        {/* Na Lixeira o botão chamava `handleStatusChange(id, 'descartado')`
                            num item que JÁ está descartado: clique sem efeito, e o
                            toast dizia "Status atualizado" (auditoria 03/09/2026).
                            O que falta ali é o contrário — trazer de volta. Restaurar
                            devolve o item para a triagem e zera o relógio do expurgo
                            (`updateItemStatus` limpa o `discardedAt`). */}
                        {trashMode && (
                          <button onClick={() => handleStatusChange(item.id, 'novo')} className="p-1 text-slate-400 hover:text-white transition-colors" title="Restaurar para a triagem">
                            <Icon name="refresh" className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {itemsMeta && itemsMeta.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-slate-400">
            {itemsMeta.total} licitacao(oes) encontrada(s)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setItemsPage(Math.max(1, itemsPage - 1))}
              disabled={!itemsMeta.hasPrevPage}
              className="p-1.5 rounded border border-central-borda bg-central-cartao text-slate-300 disabled:opacity-30 hover:bg-central-elevado transition-colors"
            >
              <Icon name="chevronLeft" className="w-4 h-4" />
            </button>
            <span className="text-sm text-slate-300">
              {itemsMeta.page} / {itemsMeta.totalPages}
            </span>
            <button
              onClick={() => setItemsPage(itemsPage + 1)}
              disabled={!itemsMeta.hasNextPage}
              className="p-1.5 rounded border border-central-borda bg-central-cartao text-slate-300 disabled:opacity-30 hover:bg-central-elevado transition-colors"
            >
              <Icon name="chevronRight" className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
);

export default EncontrarTable;
